import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Plus, X, Truck, PackageCheck, ScanLine, CheckCircle2, AlertTriangle, Boxes, Trash2 } from "lucide-react";
import ScannerInput from "@/components/ScannerInput";

const MODES = ["mixto", "aereo", "terrestre", "maritimo", "express"];
const MODE_KEY = { aereo: "air", terrestre: "road", maritimo: "sea", express: "express", mixto: "mixed" };
const MODE_TONE = { aereo: "bg-sky-600", terrestre: "bg-amber-600", maritimo: "bg-cyan-700", express: "bg-red-600", mixto: "bg-slate-700" };
const STATE_TONE = {
  abierta: "bg-amber-100 text-amber-700", en_transito: "bg-blue-100 text-blue-700",
  en_recepcion: "bg-violet-100 text-violet-700", recibida_hub: "bg-emerald-100 text-emerald-700",
  recibida_con_incidencias: "bg-red-100 text-red-700",
};
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { oficina: "", hub_destino: "", warehouse_id: "", fecha: today(), transportista: "", vehiculo: "", conductor: "", modo: "mixto", notas: "" };
const EMPTY_SCAN = { code: "", unidades_recibidas: "", peso_recibido: "", largo_recibido: "", ancho_recibido: "", alto_recibido: "", estado_fisico: "ok", nota: "" };

const Totals = ({ label, tot, tone = "bg-slate-50 border-slate-200", testid }) => (
  <div className={`rounded-lg border p-3 ${tone}`} data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</div>
    <div className="mt-1 grid grid-cols-4 gap-1 text-sm tabular-nums font-semibold">
      <span>{tot?.paquetes ?? 0}</span>
      <span>{tot?.unidades ?? 0}</span>
      <span>{tot?.peso ?? 0} kg</span>
      <span>{tot?.cbm ?? 0}</span>
    </div>
  </div>
);

export default function OfficeExits({ mode = "exits" }) {
  const reception = mode === "reception";
  const { api, user } = useAuth();
  const { t } = useI18n();
  const canCreate = ["admin", "operaciones", "sucursal"].includes(user.role);
  const canReceive = ["admin", "operaciones", "almacen"].includes(user.role);

  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [codes, setCodes] = useState("");
  const [result, setResult] = useState(null);
  const [scan, setScan] = useState(EMPTY_SCAN);
  const [scanLog, setScanLog] = useState([]);
  const [recResult, setRecResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanCode, setScanCode] = useState("");

  const load = useCallback(() => api.get("/office-exits").then(r => setRows(r.data)).catch(() => {}), [api]);
  useEffect(() => {
    load();
    api.get("/warehouses").then(r => setWarehouses(r.data || [])).catch(() => {});
  }, [load, api]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/office-exits/${id}`);
      setDetail(data); setResult(null); setRecResult(null); setScanLog([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openByCode = async (codeArg) => {
    const c = (codeArg ?? scanCode).trim();
    if (!c) return;
    try {
      const { data } = await api.get(`/office-exits/scan/${encodeURIComponent(c)}`);
      setScanCode("");
      openDetail(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/office-exits", form);
      toast.success(`${t("saved")} · ${data.codigo}`);
      setShowForm(false); setForm(EMPTY); await load(); openDetail(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const addPackages = async () => {
    const list = codes.split(/[\s,;]+/).map(c => c.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/office-exits/${detail.id}/packages`, { codes: list });
      setResult(data); setCodes("");
      if (data.added.length) toast.success(`${t("added_ok")}: ${data.added.length}`);
      data.rejected.forEach(r => toast.error(`${r.code}: ${r.motivo}`, { duration: 7000 }));
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const removePkg = async (code) => {
    try { await api.delete(`/office-exits/${detail.id}/packages/${encodeURIComponent(code)}`); await openDetail(detail.id); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const closeExit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/office-exits/${detail.id}/close`);
      toast.success(`${t("exit_en_transito")} · ${data.totales.paquetes} · ${data.totales.peso} kg`);
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const startReception = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/office-exits/${detail.id}/reception/start`);
      setScanLog(data.exit?.recepcion?.items || []);
      toast.success(t("start_reception"));
      await openDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const doScan = async (e) => {
    e?.preventDefault();
    if (!scan.code.trim()) return;
    setBusy(true);
    try {
      const body = { code: scan.code.trim(), presente: true, estado_fisico: scan.estado_fisico, nota: scan.nota };
      ["unidades_recibidas", "peso_recibido", "largo_recibido", "ancho_recibido", "alto_recibido"].forEach(k => {
        if (scan[k] !== "" && scan[k] !== null) body[k] = +scan[k];
      });
      const { data } = await api.post(`/office-exits/${detail.id}/reception/scan`, body);
      setScanLog(l => [{ ...data }, ...l]);
      if (data.status === "ok") toast.success(`${data.code} OK`);
      else if (data.status === "extra") toast.error(`${data.code}: ${t("inc_extra") || "extra"}`);
      else if (data.status === "duplicate") toast.warning(data.mensaje);
      else toast.warning(`${data.code}: ${data.diffs.map(d => `${d.campo} ${d.esperado}→${d.recibido}`).join(", ")}`);
      setScan({ ...EMPTY_SCAN });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const closeReception = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/office-exits/${detail.id}/reception/close`, { notas: "" });
      setRecResult(data);
      if (data.resultado === "RECEPCION_OK") toast.success(t("reception_ok"));
      else toast.error(`${t("reception_incidents")} · ${data.incidencias.length}`);
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const visible = reception ? rows.filter(r => ["en_transito", "en_recepcion", "recibida_hub", "recibida_con_incidencias"].includes(r.estado)) : rows;
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const scanned = new Set(scanLog.map(s => s.code));

  return (
    <div className="space-y-4" data-testid="office-exits-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            {reception ? <ScanLine className="w-5 h-5 text-violet-600" /> : <Truck className="w-5 h-5 text-indigo-600" />}
            {reception ? t("hub_reception") : t("office_exits")}
          </h2>
          <p className="text-sm text-slate-500">{visible.length}</p>
        </div>
        {canCreate && !reception && (
          <button data-testid="new-exit-btn" onClick={() => { setForm({ ...EMPTY, oficina: user.oficina || "" }); setShowForm(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("new_office_exit")}
          </button>
        )}
      </div>

      {reception && (
        <div className="bg-white border-2 border-violet-200 rounded-xl p-3 space-y-2" data-testid="rec-open-panel">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">
            <ScanLine className="w-3 h-3 inline me-1" /> {t("office_exit")}
          </label>
          <ScannerInput
            testIdPrefix="rec-open"
            placeholder="OF-00001"
            buttonLabel={t("start_reception")}
            onScan={(code) => openByCode(code)}
          />
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400" data-testid="no-exits">
          <Truck className="w-14 h-14 mx-auto opacity-30 mb-3" /><p>{t("no_office_exits")}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3 text-start">{t("office_exit")}</th>
                <th className="p-3 text-start">{t("date")}</th>
                <th className="p-3 text-start">{t("office")} → {t("hub_destination")}</th>
                <th className="p-3 text-start">{t("transport_mode")}</th>
                <th className="p-3 text-end">{t("total_packages")}</th>
                <th className="p-3 text-end">{t("total_weight")}</th>
                <th className="p-3 text-end">CBM</th>
                <th className="p-3 text-start">{t("status")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map(r => (
                <tr key={r.id} data-testid={`exit-row-${r.codigo}`}>
                  <td className="p-3 font-mono font-semibold">{r.codigo}</td>
                  <td className="p-3 tabular-nums text-slate-600">{r.fecha}</td>
                  <td className="p-3">{r.oficina || "—"} → <b>{r.hub_destino || "—"}</b></td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white ${MODE_TONE[r.modo]}`}>{t(MODE_KEY[r.modo])}</span></td>
                  <td className="p-3 text-end tabular-nums">{r.totales_declarados?.paquetes ?? 0}</td>
                  <td className="p-3 text-end tabular-nums">{r.totales_declarados?.peso ?? 0} kg</td>
                  <td className="p-3 text-end tabular-nums">{r.totales_declarados?.cbm ?? 0}</td>
                  <td className="p-3"><span data-testid={`exit-state-${r.codigo}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_TONE[r.estado] || "bg-slate-100"}`}>{t(`exit_${r.estado}`) || r.estado}</span></td>
                  <td className="p-3 text-end">
                    <button data-testid={`exit-detail-${r.codigo}`} onClick={() => openDetail(r.id)} className="text-xs font-semibold text-indigo-600 hover:underline">{t("open_detail")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ==== Create modal ==== */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3" data-testid="exit-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{t("new_office_exit")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div>
              <label className={lbl}>{t("transport_mode")} *</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="ef-modo">
                {MODES.map(m => (
                  <button key={m} type="button" data-testid={`ef-modo-${m}`} onClick={() => setForm({ ...form, modo: m })}
                    className={`py-2 rounded-lg text-xs font-semibold border ${form.modo === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300"}`}>{t(MODE_KEY[m])}</button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-1">{t("mixed_hint")}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("office")}</label><input data-testid="ef-oficina" value={form.oficina} onChange={(e) => setForm({ ...form, oficina: e.target.value })} className={inp} /></div>
              <div>
                <label className={lbl}>{t("hub_destination")}</label>
                <select data-testid="ef-hub" value={form.warehouse_id} onChange={(e) => {
                  const wh = warehouses.find(w => w.id === e.target.value);
                  setForm({ ...form, warehouse_id: e.target.value, hub_destino: wh?.nombre || "" });
                }} className={inp}>
                  <option value="">—</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}
                </select>
              </div>
              <div><label className={lbl}>{t("date")}</label><input type="date" data-testid="ef-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("carrier")}</label><input data-testid="ef-carrier" value={form.transportista} onChange={(e) => setForm({ ...form, transportista: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("vehicle")}</label><input data-testid="ef-vehiculo" value={form.vehiculo} onChange={(e) => setForm({ ...form, vehiculo: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("driver")}</label><input data-testid="ef-conductor" value={form.conductor} onChange={(e) => setForm({ ...form, conductor: e.target.value })} className={inp} /></div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="ef-submit" className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {/* ==== Detail ==== */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-5xl p-5 space-y-4 my-4" data-testid="exit-detail">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                  <span className="font-mono">{detail.codigo}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white ${MODE_TONE[detail.modo]}`}>{t(MODE_KEY[detail.modo])}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_TONE[detail.estado] || "bg-slate-100"}`}>{t(`exit_${detail.estado}`) || detail.estado}</span>
                </h3>
                <p className="text-xs text-slate-500">{detail.oficina} → {detail.hub_destino || "—"} · {detail.fecha} · {detail.transportista} · {detail.vehiculo} · {detail.conductor}</p>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Totals testid="exit-totals-declared" label={`${t("declared")} · ${t("total_packages")} / ${t("total_units")} / ${t("total_weight")} / CBM`} tot={detail.totales_declarados} />
              {detail.totales_recibidos && <Totals testid="exit-totals-received" label={`${t("received_val")} · ${t("total_packages")} / ${t("total_units")} / ${t("total_weight")} / CBM`} tot={detail.totales_recibidos} tone="bg-violet-50 border-violet-200" />}
            </div>

            {detail.estado === "abierta" && canCreate && (
              <div className="border-2 border-indigo-200 bg-indigo-50/40 rounded-lg p-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-indigo-800">{t("add_packages")}</p>
                <div className="flex gap-2 flex-wrap">
                  <input data-testid="exit-add-input" value={codes} onChange={(e) => setCodes(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPackages(); } }}
                    placeholder={t("scan_or_type")} className={`${inp} flex-1 min-w-[220px] font-mono`} />
                  <button data-testid="exit-add-btn" disabled={busy} onClick={addPackages} className="bg-indigo-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> {t("add")}</button>
                  <button data-testid="exit-close-btn" disabled={busy || !(detail.packages || []).length} onClick={closeExit} className="bg-slate-900 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"><PackageCheck className="w-4 h-4" /> {t("close_exit")}</button>
                </div>
                {result?.rejected?.length > 0 && (
                  <div className="space-y-1" data-testid="exit-rejected">
                    {result.rejected.map(r => (
                      <div key={r.code} className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b className="font-mono">{r.code}</b> — {r.motivo}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {canReceive && ["en_transito", "en_recepcion"].includes(detail.estado) && (
              <div className="border-2 border-violet-200 bg-violet-50/40 rounded-lg p-3 space-y-3" data-testid="reception-panel">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-violet-800 flex items-center gap-1.5"><ScanLine className="w-4 h-4" /> {t("hub_reception")}</p>
                  {detail.estado === "en_transito"
                    ? <button data-testid="rec-start" disabled={busy} onClick={startReception} className="bg-violet-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">{t("start_reception")}</button>
                    : <button data-testid="rec-close" disabled={busy} onClick={closeReception} className="bg-slate-900 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">{t("close_reception")}</button>}
                </div>
                {detail.estado === "en_recepcion" && (
                  <div className="space-y-2">
                    <label className={lbl}>{t("scan_package")}</label>
                    <ScannerInput
                      testIdPrefix="rec-code"
                      placeholder={t("scan_package")}
                      buttonLabel={t("add") || "OK"}
                      onScan={(code) => setScan((s) => ({ ...s, code }))}
                    />
                    <form onSubmit={doScan} className="grid grid-cols-2 sm:grid-cols-8 gap-2 items-end">
                      <div className="col-span-2 sm:col-span-2"><label className={lbl}>{t("scanned_code") || "Código"}</label><input data-testid="rec-code-input" value={scan.code} onChange={(e) => setScan({ ...scan, code: e.target.value })} className={`${inp} font-mono`} /></div>
                    <div><label className={lbl}>{t("units")}</label><input type="number" step="1" data-testid="rec-unidades" value={scan.unidades_recibidas} onChange={(e) => setScan({ ...scan, unidades_recibidas: e.target.value })} className={inp} /></div>
                    <div><label className={lbl}>{t("weight_kg")}</label><input type="number" step="0.1" data-testid="rec-peso" value={scan.peso_recibido} onChange={(e) => setScan({ ...scan, peso_recibido: e.target.value })} className={inp} /></div>
                    <div><label className={lbl}>L</label><input type="number" step="0.1" data-testid="rec-largo" value={scan.largo_recibido} onChange={(e) => setScan({ ...scan, largo_recibido: e.target.value })} className={inp} /></div>
                    <div><label className={lbl}>W</label><input type="number" step="0.1" data-testid="rec-ancho" value={scan.ancho_recibido} onChange={(e) => setScan({ ...scan, ancho_recibido: e.target.value })} className={inp} /></div>
                    <div><label className={lbl}>H</label><input type="number" step="0.1" data-testid="rec-alto" value={scan.alto_recibido} onChange={(e) => setScan({ ...scan, alto_recibido: e.target.value })} className={inp} /></div>
                    <div>
                      <label className={lbl}>{t("physical_state")}</label>
                      <select data-testid="rec-fisico" value={scan.estado_fisico} onChange={(e) => setScan({ ...scan, estado_fisico: e.target.value })} className={inp}>
                        <option value="ok">{t("state_ok")}</option>
                        <option value="danado">{t("damaged")}</option>
                      </select>
                    </div>
                    <button type="submit" disabled={busy} data-testid="rec-scan-btn" className="col-span-2 sm:col-span-8 bg-violet-600 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold">{t("scan_package")}</button>
                  </form>
                  </div>
                )}
                {scanLog.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="rec-log">
                    {scanLog.map((s, i) => (
                      <div key={`${s.code}-${i}`} className={`text-xs rounded p-2 border flex items-start gap-1.5 ${s.status === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : s.status === "extra" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                        {s.status === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />}
                        <span><b className="font-mono">{s.code}</b> — {s.status === "ok" ? "OK" : s.status === "extra" ? t("inc_extra") || "EXTRA" : (s.diffs || []).map(d => `${d.campo}: ${d.esperado} → ${d.recibido}`).join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                )}
                {recResult && (
                  <div className={`rounded-lg p-3 border ${recResult.resultado === "RECEPCION_OK" ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="rec-result">
                    <p className="font-bold text-sm">{recResult.resultado === "RECEPCION_OK" ? t("reception_ok") : t("reception_incidents")}</p>
                    <p className="text-xs mt-1">{t("expected_totals")}: {recResult.esperado.paquetes} · {recResult.esperado.peso} kg · {recResult.esperado.cbm} CBM — {t("received_totals")}: {recResult.recibido.paquetes} · {recResult.recibido.peso} kg · {recResult.recibido.cbm} CBM</p>
                    {recResult.incidencias.map(i => (
                      <div key={i.id} className="text-xs mt-1 bg-white/70 rounded p-1.5"><b className="font-mono">{i.numero}</b> · {t(`inc_${i.tipo}`) || i.tipo} — {i.descripcion}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs min-w-[820px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="p-2 text-start">#</th>
                    <th className="p-2 text-start">{t("internal_tracking")}</th>
                    <th className="p-2 text-start">{t("carrier_tracking")}</th>
                    <th className="p-2 text-start">{t("receiver")}</th>
                    <th className="p-2 text-start">{t("dest_city")}</th>
                    <th className="p-2 text-end">{t("declared")}</th>
                    <th className="p-2 text-end">{t("received_val")}</th>
                    <th className="p-2 text-start">{t("flow_state")}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(detail.packages || []).length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">—</td></tr>}
                  {(detail.packages || []).map((p, idx) => (
                    <tr key={p.codigo} data-testid={`exit-pkg-${p.codigo}`} className={scanned.has(p.codigo) ? "bg-emerald-50/60" : ""}>
                      <td className="p-2 text-slate-400">{idx + 1}</td>
                      <td className="p-2 font-mono">{p.codigo}</td>
                      <td className="p-2 font-mono text-slate-600">{p.tracking_transportista || "—"}</td>
                      <td className="p-2">{p.receptor_nombre}</td>
                      <td className="p-2">{p.destino_ciudad || p.destino || "—"}</td>
                      <td className="p-2 text-end tabular-nums">{p.numero_paquetes} · {p.peso_total} kg · {p.cbm}</td>
                      <td className="p-2 text-end tabular-nums text-violet-700">{p.recibido ? `${p.recibido.unidades} · ${p.recibido.peso} kg · ${p.recibido.cbm}` : t("pending_scan")}</td>
                      <td className="p-2"><span className="text-[10px] font-bold uppercase">{t(`fs_${p.estado_flujo}`) || p.estado_flujo}</span></td>
                      <td className="p-2 text-end">
                        {detail.estado === "abierta" && canCreate && <button data-testid={`exit-pkg-del-${p.codigo}`} onClick={() => removePkg(p.codigo)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(detail.incidencias || []).length > 0 && (
              <div className="space-y-1" data-testid="exit-incidents">
                <p className="text-xs font-bold uppercase tracking-wider text-red-700 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {t("incidents")}</p>
                {detail.incidencias.map(i => (
                  <div key={i.id} className="text-xs bg-red-50 border border-red-200 rounded p-2">
                    <b className="font-mono">{i.numero}</b> · {t(`inc_${i.tipo}`) || i.tipo} · {i.descripcion}
                    {i.bloquea && <span className="ms-2 text-[10px] font-bold uppercase bg-red-600 text-white px-1.5 py-0.5 rounded">block</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="flex items-center gap-1"><Boxes className="w-3.5 h-3.5" /> {detail.created_by_name}</span>
              <button onClick={() => setDetail(null)} className="border border-slate-300 px-4 py-2 rounded-lg font-semibold text-slate-700">{t("close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
